import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import type { SoManagerSyntheticWellLogPartsFragment } from '~/apollo/generated/v4/graphql';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { SOFormFields } from '~/components/upload/supporting-object/so-form-fields';
import type { SOParent } from '~/components/upload/supporting-object/so-manager';
import { UPDATE_SYNTHETIC_WELL_LOG } from '~/components/upload/supporting-object/so-operations';
import { useZodForm } from '~/utils/forms';
import {
  initialSyntheticWellLog,
  wellLogSchema,
} from '~/utils/modules/supporting-object';

type Props = SOParent & {
  syntheticWellLog: SoManagerSyntheticWellLogPartsFragment;
  onUpdateSuccess?: () => unknown;
};

export function SyntheticWellLogUpdateForm({
  syntheticWellLog,
  studyId,
  onUpdateSuccess,
}: Props) {
  const [updateSyntheticWellLog, { loading, error }] = useMutation(
    UPDATE_SYNTHETIC_WELL_LOG,
  );

  const form = useZodForm({
    schema: wellLogSchema,
    values: initialSyntheticWellLog(syntheticWellLog),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await updateSyntheticWellLog({
        variables: {
          id: syntheticWellLog.id,
          input: values,
        },
      });
      toast.success('Synthetic well log updated successfully.');
      form.reset();
      if (onUpdateSuccess) onUpdateSuccess();
    } catch (err) {
      console.log('Error updating synthetic well log', err);
      toast.error('There was a problem updating the synthetic well log.');
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <SOFormFields soType="SYNTHETIC_WELL_LOG" studyId={studyId} />
        <HookFormErrors graphQLError={error} />

        <div className="card-actions justify-end">
          <Button type="submit" loading={loading} color="primary">
            Save
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
